import { Link } from 'react-router-dom';
import {
    Zap,
    Package,
    ExternalLink,
    Terminal,
    MessageSquare,
    Store,
    Check,
    X,
    Code,
    Globe,
    Cpu,
    ArrowRight,
    TrendingUp,
    Scale,
    Wrench,
    BookOpen,
    Github,
} from "lucide-react";

const Nav = () => (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/20 bg-transparent">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
            {/* Logo flush left */}
            <div className="flex items-center gap-3">
                <img src="/logo.png" alt="Anymind" className="h-8 w-8" />
                <span className="text-xl font-bold tracking-tight text-foreground">Anymind</span>
            </div>
            {/* Nav links center-right */}
            <div className="hidden items-center gap-8 md:flex">
                {["Product", "Features", "Solutions", "Pricing"].map((item) => (
                    <a key={item} href="#" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                        {item}
                    </a>
                ))}
            </div>
            {/* Pill CTA */}
            <a
                href="https://chat-persistence-3.emergent.host"
                className="hidden md:inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-5 py-2 text-sm font-medium text-foreground backdrop-blur transition-all hover:border-primary/40 hover:bg-secondary/70"
            >
                Enter App <ArrowRight className="h-3.5 w-3.5" />
            </a>
        </div>
    </nav>
);

const Hero = () => (
    <section className="relative h-screen overflow-hidden bg-background pt-16">
        {/* Full-bleed video background */}
        <div className="pointer-events-none absolute inset-0">
            <video
                src="/bg.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-cover opacity-60 scale-78"
            />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_50%,transparent_40%,hsl(var(--background))_100%)]" />
        </div>

        {/* Bottom layout: stacked on mobile, split on desktop */}
        <div className="absolute bottom-0 left-0 right-0 flex flex-col gap-6 px-6 pb-10 md:flex-row md:items-end md:justify-between md:gap-8 md:px-12 md:pb-14">
            {/* Headline + tagline + avatars */}
            <div className="max-w-lg">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                    v1 is live.
                </div>
                <h1 className="text-3xl font-extrabold leading-[1.08] tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
                    Use your intelligence<br />from anywhere<br />you want.
                </h1>
                <p className="mt-3 text-sm text-muted-foreground">
                    Anymind: Programmable agents &amp; on-chain intelligence.
                </p>
                {/* Avatar social proof */}
                <div className="mt-4 flex items-center gap-3">
                    <div className="flex -space-x-2">
                        {["#374151", "#4B5563", "#1F2937", "#6B7280", "#111827"].map((bg, i) => (
                            <div key={i} className="h-7 w-7 rounded-full border-2 border-background" style={{ backgroundColor: bg }} />
                        ))}
                    </div>
                    <span className="text-xs text-muted-foreground">Trusted by developers worldwide</span>
                </div>
            </div>

            {/* Subtitle + CTAs */}
            <div className="shrink-0 md:max-w-[280px] md:text-right">
                <p className="mb-5 hidden text-sm leading-relaxed text-muted-foreground md:block">
                    Anymind turns conversations into programmable agents and on-chain intelligence assets that power open agent networks and marketplaces.
                </p>
                <div className="flex flex-row gap-3 md:flex-col md:items-end">
                    <a
                        href="https://chat-persistence-3.emergent.host"
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-foreground px-5 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90 md:flex-none"
                    >
                        Enter App <ArrowRight className="h-4 w-4" />
                    </a>
                    <Link
                        to="/marketplace"
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-border bg-secondary/30 px-5 py-3 text-sm font-medium text-foreground backdrop-blur transition-colors hover:border-primary/30 hover:bg-secondary/50 md:flex-none"
                    >
                        View Marketplace
                    </Link>
                </div>
            </div>
        </div>
    </section>
);

const featureCards = [
    { icon: Zap, title: "Runtime Engine", desc: "Secure sandboxed runtime that executes deployed intelligence capsules and agent systems with enforced CPU, memory, and execution boundaries." },
    { icon: Package, title: "Agent Packaging", desc: "Chat-built intelligence is packaged into reusable capsules containing prompts, context, vector knowledge, and programmable behavior." },
    { icon: ExternalLink, title: "On-Chain Settlement", desc: "Each query triggers transparent blockchain settlement, enabling creators to monetize their intelligence capsules directly." },
    { icon: Terminal, title: "Anymind SDK", desc: "Anymind SDK lets developers invoke capsules as agents, tools, or composable intelligence primitives." },
    { icon: MessageSquare, title: "Agent Networks", desc: "Build cognitive systems where specialized agents communicate, coordinate, and execute tasks together as distributed intelligence networks." },
    { icon: Store, title: "Marketplace", desc: "Explore a marketplace of intelligence capsules built by developers and access domain-specific AI capabilities on demand." },
];

const Features = () => (
    <section className="border-t border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Core Infrastructure</h2>
                <p className="mt-4 text-muted-foreground">Everything needed to turn conversations into programmable intelligence assets.</p>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {featureCards.map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="group rounded-xl border border-border bg-card/50 p-6 backdrop-blur transition-all duration-300 hover:border-primary/30 hover:shadow-[0_0_30px_-10px_hsl(var(--cyan)/0.15)]">
                        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-secondary/50">
                            <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-foreground">{title}</h3>
                        <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const traditionalItems = [
    "Train or fine-tune models",
    "Manage GPU infrastructure",
    "Build custom orchestration logic",
    "Deploy complex pipelines",
    "Maintain compute environments",
];

const anymindItems = [
    "Start with conversations",
    "Package intelligence instantly",
    "Deploy as capsules",
    "Query through APIs",
    "Earn from usage",
];

const Comparison = () => (
    <section className="border-t border-border bg-secondary/20 py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Built for the intelligence economy</h2>
            </div>
            <div className="grid gap-8 md:grid-cols-2">
                <div className="rounded-xl border border-border bg-card/30 p-8">
                    <h3 className="mb-6 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Traditional AI Workflow</h3>
                    <ul className="space-y-4">
                        {traditionalItems.map((item) => (
                            <li key={item} className="flex items-center gap-3 text-muted-foreground">
                                <X className="h-4 w-4 shrink-0 text-muted-foreground/50" />
                                <span className="text-sm">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="rounded-xl border border-primary/20 bg-card/50 p-8">
                    <h3 className="mb-6 text-sm font-semibold uppercase tracking-wider text-primary">Anymind Workflow</h3>
                    <ul className="space-y-4">
                        {anymindItems.map((item) => (
                            <li key={item} className="flex items-center gap-3 text-foreground">
                                <Check className="h-4 w-4 shrink-0 text-primary" />
                                <span className="text-sm font-medium">{item}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    </section>
);

const archSteps = [
    { icon: Code, label: "Developer", desc: "Creates conversations and packages them into capsules." },
    { icon: Cpu, label: "Capsule", desc: "Portable intelligence asset with metadata and pricing." },
    { icon: Globe, label: "Runtime", desc: "Secure execution environment for agent queries." },
    { icon: Store, label: "Marketplace", desc: "Global distribution layer for intelligence assets." },
];

const Architecture = () => (
    <section className="border-t border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Infrastructure for programmable intelligence</h2>
                <p className="mt-4 text-muted-foreground">Designed as a control plane for deploying, running, and monetizing intelligence assets.</p>
            </div>
            <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                <div className="absolute left-[12.5%] right-[12.5%] top-[2.25rem] hidden h-px bg-border lg:block" />
                {archSteps.map(({ icon: Icon, label, desc }, i) => (
                    <div key={label} className="relative text-center">
                        <div className="relative z-10 mx-auto mb-4 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border border-border bg-card">
                            <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <h3 className="mb-2 text-base font-semibold text-foreground">{label}</h3>
                        <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                        {i < archSteps.length - 1 && (
                            <ArrowRight className="absolute -right-4 top-8 hidden h-4 w-4 text-muted-foreground lg:block" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const useCases = [
    { icon: TrendingUp, title: "Trading Agents", desc: "Automated research agents for on-chain market analysis." },
    { icon: Scale, title: "Legal Intelligence", desc: "Capsules trained on regulatory conversations and frameworks." },
    { icon: Wrench, title: "Developer Assistants", desc: "Reusable engineering reasoning packaged as agents." },
    { icon: BookOpen, title: "Research Systems", desc: "Long-term knowledge systems built from structured discussions." },
];

const UseCases = () => (
    <section className="border-t border-border bg-secondary/20 py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="mb-16 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">What developers are building</h2>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {useCases.map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="group rounded-xl border border-border bg-card/50 p-6 backdrop-blur transition-all duration-300 hover:border-primary/30 hover:shadow-[0_0_30px_-10px_hsl(var(--cyan)/0.15)]">
                        <Icon className="mb-4 h-6 w-6 text-primary" />
                        <h3 className="mb-2 text-base font-semibold text-foreground">{title}</h3>
                        <p className="text-sm leading-relaxed text-muted-foreground">{desc}</p>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const DevExperience = () => (
    <section className="border-t border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="grid items-center gap-12 lg:grid-cols-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Developer Experience</h2>
                    <p className="mt-4 max-w-md leading-relaxed text-muted-foreground">
                        Use via pip!
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <a href="#" className="inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90">
                            <BookOpen className="h-4 w-4" /> View Documentation
                        </a>
                        <a href="#" className="inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/30 px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-primary/30">
                            <Github className="h-4 w-4" /> GitHub Repository
                        </a>
                    </div>
                </div>
                <div className="overflow-hidden rounded-xl border border-border bg-card/80">
                    <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                        <span className="h-3 w-3 rounded-full bg-destructive/60" />
                        <span className="h-3 w-3 rounded-full bg-yellow-500/60" />
                        <span className="h-3 w-3 rounded-full bg-green-500/60" />
                        <span className="ml-4 text-xs text-muted-foreground">main.py</span>
                    </div>
                    <pre className="overflow-x-auto p-6 text-sm leading-relaxed">
                        <code>
                            <span className="text-primary">from</span> <span className="text-foreground">anymind</span> <span className="text-primary">import</span> <span className="text-foreground">Agent</span>{"\n\n"}
                            <span className="text-foreground">agent</span> = <span className="text-foreground">Agent</span>({"(\n"}
                            {"    "}<span className="text-foreground">chat_id</span>=<span className="text-green-400">""</span>,{"\n"}
                            {"    "}<span className="text-foreground">backend_url</span>=<span className="text-green-400">"https://chat-persistence-3.emergent.host"</span>{"\n"}
                            {")\n\n"}
                            <span className="text-foreground">response</span> = <span className="text-foreground">agent.chat</span>(<span className="text-green-400">"Your message"</span>){"\n"}
                            <span className="text-primary">print</span>(<span className="text-foreground">response</span>)
                        </code>
                    </pre>
                </div>
            </div>
        </div>
    </section>
);

const CTA = () => (
    <section className="border-t border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card/80 px-8 py-20 text-center">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--cyan)/0.08)_0%,transparent_70%)]" />
                <div className="relative">
                    <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Start building the intelligence layer.</h2>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <a href="https://chat-persistence-3.emergent.host" className="rounded-lg bg-foreground px-6 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90">
                            Enter App
                        </a>
                        <Link to="/marketplace" className="rounded-lg border border-border bg-secondary/30 px-6 py-3 text-sm font-medium text-foreground backdrop-blur transition-colors hover:border-primary/30">
                            View Marketplace
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const footerLinks = {
    Product: ["Marketplace", "SDK", "Docs"],
    Developers: ["GitHub", "API Reference"],
    Community: ["Discord", "Twitter"],
    Legal: ["Terms", "Privacy"],
};

const Footer = () => (
    <footer className="border-t border-border bg-background py-16">
        <div className="mx-auto max-w-6xl px-6">
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
                <div className="lg:col-span-1">
                    <span className="text-lg font-bold text-foreground">Anymind</span>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        Infrastructure for programmable intelligence.
                    </p>
                </div>
                {Object.entries(footerLinks).map(([heading, links]) => (
                    <div key={heading}>
                        <h4 className="mb-4 text-sm font-semibold text-foreground">{heading}</h4>
                        <ul className="space-y-3">
                            {links.map((link) => (
                                <li key={link}>
                                    <a href="#" className="text-sm text-muted-foreground transition-colors hover:text-primary">{link}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
                <span className="text-sm text-muted-foreground">© 2026 Anymind</span>
                <div className="flex gap-6">
                    <a href="#" className="text-sm text-muted-foreground transition-colors hover:text-primary">Terms</a>
                    <a href="#" className="text-sm text-muted-foreground transition-colors hover:text-primary">Privacy</a>
                </div>
            </div>
        </div>
    </footer>
);

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-background text-foreground">
            <Nav />
            <Hero />
            <Features />
            <Comparison />
            <Architecture />
            <UseCases />
            <DevExperience />
            <CTA />
            <Footer />
        </div>
    );
}
